/**
 * `gltfMaterialExport.ts` — the material + texture rows a web import writes.
 *
 * Port of what `GLTFastImporter.PostprocessMaterials` + `CreateMaterialDataAsync`
 * (`ProjectModel_UploadAsset.cs`) persist for a Unity import. The desktop client
 * loads every asset with `ImportTextures = false`, so the GLB's own materials
 * never reach its screen: what it renders is the `MaterialData` rows named by
 * each part's `MaterialIDs` and the PNG/JPG objects at `textures/{id}`. Without
 * them a web-imported model renders blank there (AI-1494).
 *
 * The GLB is still uploaded verbatim with its embedded images; these rows are
 * written BESIDE it.
 *
 * Conversion (Unity's, for the two material kinds three's `GLTFLoader` builds):
 *
 * - metallic-roughness → `Campfire/Lit`, Metallic workflow. `_BaseMap`,
 *   `_BumpMap` and `_EmissionMap` keep the source image; `_OcclusionMap` is
 *   repacked `(r, r, r, 1)` and `_MetallicGlossMap` `(b, 0, 0, 1 − g)` —
 *   metallic in R and smoothness in A, which is what URP samples.
 * - `KHR_materials_unlit` → `Shader Graphs/CampfireUnlit` (`_BaseMap` only).
 *
 * Colours are written as sRGB display values (`MaterialData` convention, see
 * `models/materials/CLAUDE.md`); three holds them linear.
 *
 * Deviations:
 * - `Cutoff` is the glTF `alphaCutoff` for a MASK material and 0 otherwise.
 *   Both clients alpha-test every `Campfire/Lit` row, so carrying the 0.5
 *   default onto an OPAQUE material would clip a texture whose alpha means
 *   nothing.
 * - A `KHR_texture_transform` is written in Unity's UV convention
 *   (`offset.y = 1 − scale.y − offset.y`), like every desktop row, which the
 *   web builder applies verbatim — the same divergence existing desktop rows
 *   with a transform already have on web.
 * - A compressed (KTX2) texture has no readable pixels and is skipped with a
 *   warning: that slot renders untextured on desktop.
 */

import {
  Color,
  DoubleSide,
  NearestFilter,
  LinearMipmapLinearFilter,
  type Material,
  type Mesh,
  type Object3D,
  type Texture,
} from "three";
import type { GLTF } from "three/addons/loaders/GLTFLoader.js";
import { zlib } from "fflate";
import type { MaterialDto } from "@models/data/materialDto";
import { DEFAULT_MATERIAL_SHADER } from "@models/materials/materialShaderVariants";
import { randomUuid } from "@models/utils/uuid";
import { fitWithinTextureBudget, textureBudgetMaxDimension } from "./textureBudget";

const K_UNLIT_SHADER = "Shader Graphs/CampfireUnlit";
const K_SURFACE_OPAQUE = 0;
const K_SURFACE_TRANSPARENT = 1;
const K_CULL_OFF = 0;
const K_CULL_BACK = 2;
const K_WORKFLOW_METALLIC = 1;
const K_PNG_ZLIB_LEVEL = 4;

export interface ExportedTexture {
  readonly id: string;
  readonly blob: Blob;
}

export interface GltfMaterialExport {
  readonly materials: ReadonlyArray<MaterialDto>;
  readonly textures: ReadonlyArray<ExportedTexture>;
  /** Each mesh node's own materials, in slot order. Non-mesh nodes are absent. */
  readonly materialIdsByNode: ReadonlyMap<Object3D, ReadonlyArray<string>>;
}

export interface GltfMaterialExportOptions {
  /** Original bytes of an external image URI (loose `.gltf` / `.gltfzip`). */
  readonly resolveUri?: (uri: string) => Blob | null;
  readonly signal?: AbortSignal;
}

/** The repacks a slot may need; `plain` keeps the source image. */
type TextureKind = "plain" | "occlusion" | "metallicGloss";

/** The fields of three's glTF materials this reads (Standard / Physical / Basic). */
interface GltfThreeMaterial extends Material {
  readonly isMeshStandardMaterial?: boolean;
  readonly isMeshBasicMaterial?: boolean;
  readonly color?: Color;
  readonly map?: Texture | null;
  readonly normalMap?: Texture | null;
  readonly normalScale?: { x: number };
  readonly aoMap?: Texture | null;
  readonly aoMapIntensity?: number;
  readonly emissive?: Color;
  readonly emissiveIntensity?: number;
  readonly emissiveMap?: Texture | null;
  readonly metalness?: number;
  readonly metalnessMap?: Texture | null;
  readonly roughness?: number;
  readonly roughnessMap?: Texture | null;
}

interface GltfJsonImage {
  readonly uri?: string;
  readonly bufferView?: number;
  readonly mimeType?: string;
}

interface GltfJsonTexture {
  readonly source?: number;
  readonly extensions?: Record<string, { readonly source?: number } | undefined>;
}

interface GltfParserLike {
  readonly json: { readonly images?: GltfJsonImage[]; readonly textures?: GltfJsonTexture[] };
  readonly associations: Map<object, { textures?: number } | undefined>;
  getDependency(type: "bufferView", index: number): Promise<ArrayBuffer>;
}

/**
 * Builds the rows for every distinct material on `nodes`' meshes. Must run
 * while `gltf.parser` is still attached — the texture budget belt strips it
 * when the asset is cached.
 */
export async function exportGltfMaterialsAsync(
  gltf: GLTF,
  nodes: Iterable<Object3D>,
  options: GltfMaterialExportOptions = {},
): Promise<GltfMaterialExport> {
  const exporter = new GltfMaterialExporter(gltf.parser as unknown as GltfParserLike | undefined, options);
  const materialIdsByNode = new Map<Object3D, string[]>();
  for (const node of nodes) {
    const mesh = node as Mesh;
    if (!mesh.isMesh) continue;
    const slots = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const ids: string[] = [];
    for (const material of slots) {
      if (!material) continue;
      ids.push(await exporter.materialId(material as GltfThreeMaterial));
    }
    if (ids.length > 0) materialIdsByNode.set(node, ids);
  }
  return {
    materials: exporter.materials,
    textures: exporter.textures,
    materialIdsByNode,
  };
}

class GltfMaterialExporter {
  readonly materials: MaterialDto[] = [];
  readonly textures: ExportedTexture[] = [];

  private readonly m_materialIds = new Map<Material, string>();
  private readonly m_textureIds = new Map<string, string | null>();
  private m_textureIndexBySource: Map<string, number> | null = null;

  constructor(
    private readonly m_parser: GltfParserLike | undefined,
    private readonly m_options: GltfMaterialExportOptions,
  ) {}

  async materialId(material: GltfThreeMaterial): Promise<string> {
    const existing = this.m_materialIds.get(material);
    if (existing) return existing;
    const id = randomUuid();
    this.m_materialIds.set(material, id);
    this.materials.push(
      material.isMeshBasicMaterial
        ? await this.unlitRow(id, material)
        : await this.litRow(id, material),
    );
    return id;
  }

  private async litRow(id: string, material: GltfThreeMaterial): Promise<MaterialDto> {
    const textures: Record<string, string> = {};
    const filterModes: Record<string, string> = {};
    const bind = async (slot: string, texture: Texture | null | undefined, kind: TextureKind) => {
      if (!texture) return;
      const textureId = await this.textureId(texture, kind);
      if (!textureId) return;
      textures[slot] = textureId;
      filterModes[slot] = filterModeOf(texture);
    };
    const metallicGloss = material.metalnessMap ?? material.roughnessMap ?? null;
    await bind("_BaseMap", material.map, "plain");
    await bind("_BumpMap", material.normalMap, "plain");
    await bind("_OcclusionMap", material.aoMap, "occlusion");
    await bind("_MetallicGlossMap", metallicGloss, "metallicGloss");
    await bind("_EmissionMap", material.emissiveMap, "plain");

    let smoothness = 1 - (material.roughness ?? 1);
    if (smoothness <= 0 && textures._MetallicGlossMap) smoothness = 1;
    const emissive = (material.emissive ?? new Color(0, 0, 0))
      .clone()
      .multiplyScalar(material.emissiveIntensity ?? 1);
    const hasEmission = emissive.r > 0 || emissive.g > 0 || emissive.b > 0 || Boolean(textures._EmissionMap);

    return {
      ...baseRow(id, material, DEFAULT_MATERIAL_SHADER),
      WorkflowMode: K_WORKFLOW_METALLIC,
      Surface: material.transparent ? K_SURFACE_TRANSPARENT : K_SURFACE_OPAQUE,
      Cull: material.side === DoubleSide ? K_CULL_OFF : K_CULL_BACK,
      Smoothness: smoothness,
      Metallic: material.metalness ?? 1,
      SpecColor: { r: 0.2, g: 0.2, b: 0.2, a: 1 },
      EmissionColor: displayColor(emissive, hasEmission ? 1 : 0),
      BumpScale: textures._BumpMap ? Math.abs(material.normalScale?.x ?? 1) : 1,
      OcclusionStrength: material.aoMapIntensity ?? 1,
      Textures: textures,
      FilterModes: filterModes,
      ...unityTransformOf(material.map),
    };
  }

  private async unlitRow(id: string, material: GltfThreeMaterial): Promise<MaterialDto> {
    const textures: Record<string, string> = {};
    const filterModes: Record<string, string> = {};
    if (material.map) {
      const textureId = await this.textureId(material.map, "plain");
      if (textureId) {
        textures._BaseMap = textureId;
        filterModes._BaseMap = filterModeOf(material.map);
      }
    }
    return {
      ...baseRow(id, material, K_UNLIT_SHADER),
      Textures: textures,
      FilterModes: filterModes,
      ...unityTransformOf(material.map),
    };
  }

  private async textureId(texture: Texture, kind: TextureKind): Promise<string | null> {
    // A texture's `source` is shared by the clones the loader makes per UV
    // channel, so one image is uploaded once per repack whatever samples it.
    const key = `${kind}|${texture.source.uuid}`;
    if (this.m_textureIds.has(key)) return this.m_textureIds.get(key) ?? null;
    let blob: Blob | null = null;
    try {
      blob = await this.encodeTexture(texture, kind);
    } catch (err) {
      console.warn(`[gltfMaterialExport] texture "${texture.name}" could not be exported:`, err);
    }
    this.m_options.signal?.throwIfAborted();
    const id = blob ? randomUuid() : null;
    if (blob && id) this.textures.push({ id, blob });
    this.m_textureIds.set(key, id);
    return id;
  }

  private async encodeTexture(texture: Texture, kind: TextureKind): Promise<Blob | null> {
    if ((texture as { isCompressedTexture?: boolean }).isCompressedTexture) {
      console.warn(`[gltfMaterialExport] compressed texture "${texture.name}" skipped — no readable pixels.`);
      return null;
    }
    const original = await this.originalImageBytes(texture);
    if (kind === "plain" && original && isPngOrJpeg(await magicOf(original))) {
      return original;
    }
    const pixels = await rasterize(original, texture);
    if (!pixels) return null;
    if (kind === "occlusion") repackOcclusion(pixels.data);
    else if (kind === "metallicGloss") repackMetallicGloss(pixels.data);
    return encodePngAsync(pixels.width, pixels.height, pixels.data);
  }

  /**
   * The image's bytes as the FILE carries them, so a texture the loader
   * downscaled to the device budget is still uploaded at full size.
   */
  private async originalImageBytes(texture: Texture): Promise<Blob | null> {
    const parser = this.m_parser;
    if (!parser) return null;
    const textureIndex = this.textureIndexOf(texture);
    if (textureIndex === undefined) return null;
    const def = parser.json.textures?.[textureIndex];
    if (!def) return null;
    const candidates = [def.source, ...Object.values(def.extensions ?? {}).map((ext) => ext?.source)];
    for (const imageIndex of candidates) {
      if (imageIndex === undefined) continue;
      const image = parser.json.images?.[imageIndex];
      if (!image) continue;
      const blob = await this.imageBlob(image);
      if (blob && blob.size > 0) return blob;
    }
    return null;
  }

  private async imageBlob(image: GltfJsonImage): Promise<Blob | null> {
    const type = image.mimeType ?? "";
    if (image.bufferView !== undefined && this.m_parser) {
      const bytes = await this.m_parser.getDependency("bufferView", image.bufferView);
      return new Blob([bytes], { type });
    }
    if (!image.uri) return null;
    if (image.uri.startsWith("data:")) {
      return (await fetch(image.uri)).blob();
    }
    return this.m_options.resolveUri?.(image.uri) ?? null;
  }

  private textureIndexOf(texture: Texture): number | undefined {
    const direct = this.m_parser?.associations.get(texture)?.textures;
    if (direct !== undefined) return direct;
    if (!this.m_textureIndexBySource) {
      this.m_textureIndexBySource = new Map();
      for (const [key, value] of this.m_parser?.associations ?? []) {
        const source = (key as Partial<Texture>).source;
        if (value?.textures !== undefined && source) {
          this.m_textureIndexBySource.set(source.uuid, value.textures);
        }
      }
    }
    return this.m_textureIndexBySource.get(texture.source.uuid);
  }
}

function baseRow(id: string, material: GltfThreeMaterial, shader: string): MaterialDto {
  // Every field desktop `ToMaterialData` reads with an unguarded indexer, at
  // the value Unity's own `MaterialData` defaults to when the shader lacks it.
  return {
    ID: id,
    Name: material.name || id,
    ShaderName: shader,
    UserPreset: false,
    WorkflowMode: 0,
    Surface: K_SURFACE_OPAQUE,
    Cull: K_CULL_OFF,
    Smoothness: 0,
    Metallic: 0,
    BaseColor: displayColor(material.color ?? new Color(1, 1, 1), material.opacity),
    SpecColor: { r: 0, g: 0, b: 0, a: 0 },
    EmissionColor: { r: 0, g: 0, b: 0, a: 0 },
    FresnelColor: { r: 0, g: 0, b: 0, a: 0 },
    FresnelPower: 0,
    BumpScale: 0,
    SmoothnessLow: 0,
    OcclusionStrength: 0,
    ClearCoat: 0,
    ClearCoatGlossiness: 0,
    ClearCoatGlossinessScale: 0,
    Cutoff: material.alphaTest > 0 ? material.alphaTest : 0,
    UseVertexColor: 0,
    Tiling: { x: 1, y: 1 },
    Offset: { x: 0, y: 0 },
  };
}

function displayColor(color: Color, alpha: number): { r: number; g: number; b: number; a: number } {
  const srgb = color.clone().convertLinearToSRGB();
  return { r: srgb.r, g: srgb.g, b: srgb.b, a: alpha };
}

function filterModeOf(texture: Texture): string {
  if (texture.magFilter === NearestFilter) return "Point";
  return texture.minFilter === LinearMipmapLinearFilter ? "Trilinear" : "Bilinear";
}

function unityTransformOf(map: Texture | null | undefined): Pick<MaterialDto, "Tiling" | "Offset"> {
  if (!map) return {};
  const { repeat, offset } = map;
  if (repeat.x === 1 && repeat.y === 1 && offset.x === 0 && offset.y === 0) return {};
  return {
    Tiling: { x: repeat.x, y: repeat.y },
    Offset: { x: offset.x, y: 1 - repeat.y - offset.y },
  };
}

async function magicOf(blob: Blob): Promise<Uint8Array> {
  return new Uint8Array(await blob.slice(0, 4).arrayBuffer());
}

function isPngOrJpeg(magic: Uint8Array): boolean {
  const png = magic[0] === 0x89 && magic[1] === 0x50 && magic[2] === 0x4e && magic[3] === 0x47;
  const jpeg = magic[0] === 0xff && magic[1] === 0xd8 && magic[2] === 0xff;
  return png || jpeg;
}

interface Pixels {
  readonly width: number;
  readonly height: number;
  readonly data: Uint8ClampedArray;
}

type DrawableImage = ImageBitmap | HTMLImageElement | HTMLCanvasElement | OffscreenCanvas;

/** Straight-alpha RGBA of the original bytes, else of the decoded image. */
async function rasterize(original: Blob | null, texture: Texture): Promise<Pixels | null> {
  let image: DrawableImage | null = null;
  let owned: ImageBitmap | null = null;
  if (original) {
    try {
      owned = await createImageBitmap(original, {
        premultiplyAlpha: "none",
        colorSpaceConversion: "none",
      });
      image = owned;
    } catch {
      // Undecodable here — fall through to the loader's decoded copy.
    }
  }
  image ??= drawableOf(texture.image);
  if (!image) return null;
  try {
    const sourceWidth = image.width;
    const sourceHeight = image.height;
    if (!sourceWidth || !sourceHeight) return null;
    const fit = fitWithinTextureBudget(sourceWidth, sourceHeight, textureBudgetMaxDimension());
    const width = fit?.width ?? sourceWidth;
    const height = fit?.height ?? sourceHeight;
    const canvas = createCanvas(width, height);
    const context = canvas.getContext("2d", { willReadFrequently: true }) as
      | CanvasRenderingContext2D
      | OffscreenCanvasRenderingContext2D
      | null;
    if (!context) return null;
    context.drawImage(image, 0, 0, width, height);
    return { width, height, data: context.getImageData(0, 0, width, height).data };
  } finally {
    owned?.close();
  }
}

function drawableOf(image: unknown): DrawableImage | null {
  if (typeof ImageBitmap !== "undefined" && image instanceof ImageBitmap) return image;
  if (typeof HTMLImageElement !== "undefined" && image instanceof HTMLImageElement) return image;
  if (typeof HTMLCanvasElement !== "undefined" && image instanceof HTMLCanvasElement) return image;
  if (typeof OffscreenCanvas !== "undefined" && image instanceof OffscreenCanvas) return image;
  return null;
}

function createCanvas(width: number, height: number): HTMLCanvasElement | OffscreenCanvas {
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(width, height);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

/** glTF occlusion is R; URP samples G — write it to every colour channel. */
function repackOcclusion(data: Uint8ClampedArray): void {
  for (let i = 0; i < data.length; i += 4) {
    const occlusion = data[i];
    data[i + 1] = occlusion;
    data[i + 2] = occlusion;
    data[i + 3] = 255;
  }
}

/** glTF metallic B / roughness G → URP metallic R / smoothness A. */
function repackMetallicGloss(data: Uint8ClampedArray): void {
  for (let i = 0; i < data.length; i += 4) {
    const roughness = data[i + 1];
    data[i] = data[i + 2];
    data[i + 1] = 0;
    data[i + 2] = 0;
    data[i + 3] = 255 - roughness;
  }
}

/**
 * RGBA8 PNG, straight alpha. A canvas `toBlob` would premultiply on the way
 * out and zero the colour of every pixel whose smoothness (alpha) is 0.
 */
async function encodePngAsync(width: number, height: number, rgba: Uint8ClampedArray): Promise<Blob> {
  const stride = width * 4;
  const raw = new Uint8Array((stride + 1) * height);
  for (let y = 0; y < height; y++) {
    raw[y * (stride + 1)] = 0;
    raw.set(rgba.subarray(y * stride, (y + 1) * stride), y * (stride + 1) + 1);
  }
  const compressed = await new Promise<Uint8Array>((resolve, reject) => {
    zlib(raw, { level: K_PNG_ZLIB_LEVEL }, (err, data) => (err ? reject(err) : resolve(data)));
  });
  const header = new Uint8Array(13);
  const view = new DataView(header.buffer);
  view.setUint32(0, width);
  view.setUint32(4, height);
  header[8] = 8; // bit depth
  header[9] = 6; // RGBA
  const signature = new Uint8Array([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);
  return new Blob(
    [signature, pngChunk("IHDR", header), pngChunk("IDAT", compressed), pngChunk("IEND", new Uint8Array(0))],
    { type: "image/png" },
  );
}

function pngChunk(type: string, data: Uint8Array): Uint8Array<ArrayBuffer> {
  const chunk = new Uint8Array(new ArrayBuffer(12 + data.length));
  const view = new DataView(chunk.buffer);
  view.setUint32(0, data.length);
  for (let i = 0; i < 4; i++) chunk[4 + i] = type.charCodeAt(i);
  chunk.set(data, 8);
  view.setUint32(8 + data.length, crc32(chunk.subarray(4, 8 + data.length)));
  return chunk;
}

let s_crcTable: Uint32Array | null = null;

function crc32(bytes: Uint8Array): number {
  if (!s_crcTable) {
    s_crcTable = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      s_crcTable[n] = c >>> 0;
    }
  }
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) crc = s_crcTable[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}
